// Core Enums
export enum UserRole {
  // System Level Roles
  ROOT_USER = 'root_user',           // Can do everything, manage organizations
  ADMIN = 'admin',                   // Organization level admin
  SUPER_MANAGER = 'super_manager',   // Access to Projects, Timesheets, Leaves
  
  // HR Roles
  HR_ADMIN = 'hr_admin',             // HR Admin with employee records access
  HR_USER = 'hr_user',               // HR user with limited employee access
  
  // Project Management Roles
  PROJECT_MANAGER = 'project_manager', // Project level management
  PRODUCT_OWNER = 'product_owner',   // Product ownership
  SCRUM_MASTER = 'scrum_master',     // Scrum process management
  
  // Employee Roles
  EMPLOYEE = 'employee',             // Basic employee
  MANAGER = 'manager',               // Team manager
  CONTRACTOR = 'contractor',         // External contractor
  FREELANCER = 'freelancer',         // Freelancer
  INTERN = 'intern',                 // Intern
  PART_TIME = 'part_time'            // Part-time employee
}

export enum Priority {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low'
}

export enum IssueType {
  EPIC = 'epic',
  STORY = 'story',
  TASK = 'task',
  SUBTASK = 'subtask',
  BUG = 'bug',
  IMPROVEMENT = 'improvement'
}

export enum IssueStatus {
  BACKLOG = 'backlog',
  TODO = 'todo',
  IN_PROGRESS = 'in_progress',
  IN_REVIEW = 'in_review',
  TESTING = 'testing',
  DONE = 'done',
  CLOSED = 'closed',
  CANCELLED = 'cancelled'
}

export enum SprintState {
  PLANNING = 'planning',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum TeamRole {
  TEAM_LEAD = 'team_lead',
  MEMBER = 'member',
  OBSERVER = 'observer'
}

export enum ReleaseStatus {
  PLANNING = 'planning',
  IN_PROGRESS = 'in_progress',
  RELEASED = 'released',
  CANCELLED = 'cancelled'
}

export enum LeaveType {
  FULL_DAY = 'full_day',
  HALF_DAY = 'half_day',
  HOURS = 'hours'
}

export enum LeaveStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export enum TimesheetStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export enum ApprovalAction {
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  RESUBMITTED = 'resubmitted'
}

export enum LinkType {
  BLOCKS = 'blocks',
  IS_BLOCKED_BY = 'is_blocked_by',
  DUPLICATES = 'duplicates',
  IS_DUPLICATED_BY = 'is_duplicated_by',
  RELATES_TO = 'relates_to',
  IS_CHILD_OF = 'is_child_of',
  IS_PARENT_OF = 'is_parent_of',
  CAUSES = 'causes',
  IS_CAUSED_BY = 'is_caused_by'
}

// Entity Interfaces
export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  website?: string;
  logo?: string;
  settings?: {
    timezone?: string;
    currency?: string;
    dateFormat?: string;
    workingDays?: number[];
    hoursPerDay?: number;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  title?: string;
  department?: string;
  phone?: string;
  role: UserRole;
  preferences?: {
    theme?: 'light' | 'dark';
    notifications?: {
      email?: boolean;
      push?: boolean;
      slack?: boolean;
    };
    timezone?: string;
  };
  isActive: boolean;
  lastLoginAt?: string;
  timeTrackerUserId?: string;
  organizationId: string;
  organization?: Organization;
  teamMemberships?: TeamMember[];
  projectAssignments?: ProjectAssignment[];
  createdAt: string;
  updatedAt: string;
  fullName: string;
}

export interface Project {
  id: string;
  name: string;
  key: string;
  storyPrefix?: string;
  description?: string;
  avatar?: string;
  methodology: 'scrum' | 'kanban' | 'hybrid';
  leadId: string;
  settings?: {
    storyPointScale?: number[];
    defaultAssignee?: string;
    issueTypes?: string[];
    customFields?: any[];
    workflowStates?: string[];
  };
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  timeTrackerProjectId?: string;
  organizationId: string;
  organization?: Organization;
  issues?: Issue[];
  sprints?: Sprint[];
  teams?: Team[];
  releases?: Release[];
  boards?: Board[];
  createdAt: string;
  updatedAt: string;
}

export interface Issue {
  id: string;
  issueNumber: number;
  title: string;
  description?: string;
  type: IssueType;
  status: string;
  priority: Priority;
  storyPoints?: number;
  originalEstimate?: number;
  remainingEstimate?: number;
  timeSpent: number;
  acceptanceCriteria?: string;
  customFields?: any;
  dueDate?: string;
  resolvedAt?: string;
  closedAt?: string;
  labels?: string[];
  orderIndex: number;
  timeTrackerTaskId?: string;
  projectId: string;
  project?: Project;
  reporterId: string;
  reporter?: User;
  assigneeId?: string;
  assignee?: User;
  sprintId?: string;
  sprint?: Sprint;
  epicId?: string;
  epic?: Epic;
  parentId?: string;
  parent?: Issue;
  subtasks?: Issue[];
  comments?: Comment[];
  attachments?: Attachment[];
  timeLogs?: TimeLog[];
  outwardLinks?: IssueLink[];
  inwardLinks?: IssueLink[];
  assignments?: IssueAssignment[];
  createdAt: string;
  updatedAt: string;
  issueKey: string;
  isOverdue: boolean;
  completionPercentage: number;
}

export interface Epic {
  id: string;
  name: string;
  description?: string;
  status: string;
  priority: Priority;
  budget?: number;
  startDate?: string;
  targetDate?: string;
  goals?: string[];
  successMetrics?: Array<{
    metric: string;
    target: string;
    current?: string;
  }>;
  color?: string;
  projectId: string;
  project?: Project;
  ownerId?: string;
  owner?: User;
  releaseId?: string;
  release?: Release;
  issues?: Issue[];
  createdAt: string;
  updatedAt: string;
  completionPercentage: number;
  totalStoryPoints: number;
  completedStoryPoints: number;
}

export interface Sprint {
  id: string;
  name: string;
  goal?: string;
  state: SprintState;
  startDate: string;
  endDate: string;
  capacity?: number;
  plannedStoryPoints?: number;
  completedStoryPoints: number;
  sprintMetrics?: {
    velocity?: number;
    burndownData?: BurndownPoint[];
    dailyProgress?: Array<{
      date: string;
      completed: number;
      added: number;
      removed: number;
    }>;
  };
  completedAt?: string;
  projectId: string;
  project?: Project;
  releaseId?: string;
  release?: Release;
  boardId?: string;
  board?: {
    id: string;
    name: string;
  };
  issues?: Issue[];
  epics?: Epic[];
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  isCompleted: boolean;
  durationInDays: number;
  progressPercentage: number;
  remainingDays: number;
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  capacity?: number;
  velocity?: number;
  workingHours?: {
    [key: string]: { start: string; end: string; };
  };
  isActive: boolean;
  projectId: string;
  project?: Project;
  members?: TeamMember[];
  createdAt: string;
  updatedAt: string;
  memberCount: number;
}

export interface TeamMember {
  id: string;
  role: TeamRole;
  capacity?: number;
  isActive: boolean;
  joinedAt?: string;
  leftAt?: string;
  teamId: string;
  team?: Team;
  userId: string;
  user?: User;
  createdAt: string;
}

export interface Release {
  id: string;
  name: string;
  version: string;
  description?: string;
  status: ReleaseStatus;
  startDate?: string;
  targetDate: string;
  releaseDate?: string;
  releaseNotes?: {
    features?: string[];
    bugFixes?: string[];
    improvements?: string[];
    breaking?: string[];
  };
  deploymentInfo?: {
    environment?: string;
    deployedBy?: string;
    deploymentDate?: Date;
    rollbackPlan?: string;
  };
  projectId: string;
  project?: Project;
  epics?: Epic[];
  sprints?: Sprint[];
  createdAt: string;
  updatedAt: string;
  isOverdue: boolean;
  completionPercentage: number;
}

export interface Board {
  id: string;
  name: string;
  type: 'kanban' | 'sprint' | 'scrum' | 'bau';
  config?: {
    swimlanes?: 'none' | 'assignee' | 'epic' | 'priority';
    quickFilters?: Array<{
      name: string;
      query: string;
      isEnabled: boolean;
    }>;
    estimation?: 'none' | 'story_points' | 'time';
    workingDays?: number[];
    sprintSettings?: {
      defaultSprintDuration?: number;
      storyPointScale?: number[];
      velocityTarget?: number;
    };
    ragSettings?: {
      enabled?: boolean;
      amberThresholdDays?: number;
      redGracePeriodDays?: number;
    };
  };
  isDefault: boolean;
  projectId: string;
  project?: Project;
  columns?: BoardColumn[];
  members?: BoardMember[];
  sprints?: Sprint[];
  createdAt: string;
  updatedAt: string;
}

export interface BoardColumn {
  id: string;
  name: string;
  status: IssueStatus;
  orderIndex: number;
  wipLimit?: number;
  color?: string;
  isCollapsed: boolean;
  boardId: string;
  board?: Board;
}

export interface Comment {
  id: string;
  content: string;
  isInternal: boolean;
  mentions?: string[];
  issueId: string;
  issue?: Issue;
  authorId: string;
  author?: User;
  createdAt: string;
  updatedAt: string;
}

export interface Attachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
  issueId: string;
  issue?: Issue;
  uploadedById: string;
  uploadedBy?: User;
  createdAt: string;
  sizeFormatted: string;
  isImage: boolean;
}

export interface TimeLog {
  id: string;
  timeSpent: number;
  description?: string;
  workDate: string;
  startTime?: string;
  endTime?: string;
  timeTrackerEntryId?: string;
  isBillable: boolean;
  billableRate?: number;
  issueId: string;
  issue?: Issue;
  userId: string;
  user?: User;
  createdAt: string;
  updatedAt: string;
  billableAmount: number;
}

export interface IssueLink {
  id: string;
  linkType: LinkType;
  description?: string;
  sourceIssueId: string;
  sourceIssue?: Issue;
  targetIssueId: string;
  targetIssue?: Issue;
  createdById: string;
  createdBy?: User;
  createdAt: string;
}

export interface Leave {
  id: string;
  userId: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  hours?: number;
  reason: string;
  status: LeaveStatus;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  user?: User;
  approver?: User;
  createdAt: string;
  updatedAt: string;
}

export interface TimesheetItem {
  id: string;
  timesheetId: string;
  projectId: string;
  taskId?: string;
  taskDescription: string;
  mondayHours: number;
  tuesdayHours: number;
  wednesdayHours: number;
  thursdayHours: number;
  fridayHours: number;
  saturdayHours: number;
  sundayHours: number;
  description?: string;
  billable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TimesheetApprovalHistory {
  id: string;
  timesheetId: string;
  action: ApprovalAction;
  performedBy: string;
  reason?: string;
  comments?: string;
  performedAt: string;
  performer?: User;
}

export interface ProjectAssignment {
  id: string;
  userId: string;
  projectId: string;
  role: string;
  isActive: boolean;
  assignedAt: string;
  user?: User;
  project?: Project;
}

export interface BoardMember {
  id: string;
  userId: string;
  boardId: string;
  assignedAt: string;
  isActive: boolean;
  user?: User;
  board?: Board;
}

export interface IssueAssignment {
  id: string;
  issueId: string;
  userId: string;
  role: string;
  isActive: boolean;
  assignedAt: string;
  user?: User;
  issue?: Issue;
}

export interface Timesheet {
  id: string;
  userId: string;
  fromDate: string;
  toDate: string;
  status: TimesheetStatus;
  rejectionReason?: string;
  rejectionComments?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  user?: User;
  items?: TimesheetItem[];
  createdAt: string;
  updatedAt: string;
}

// Dashboard and Analytics Types
export interface BurndownPoint {
  date: string;
  remaining: number;
  ideal: number;
  completed?: number;
}

export interface ProjectMetrics {
  // Basic metrics
  totalIssues: number;
  completedIssues: number;
  activeIssues: number;
  overdue: number;
  completionRate: number;
  
  // Story points metrics
  totalStoryPoints: number;
  completedStoryPoints: number;
  activeStoryPoints: number;
  storyPointsCompletionRate: number;
  
  // Sprint metrics
  totalSprints: number;
  activeSprints: number;
  completedSprintsCount: number;
  velocity: number;
  
  // Team metrics
  uniqueAssignees: number;
  issuesByAssignee: Record<string, number>;
  
  // Epic metrics
  totalEpics: number;
  completedEpics: number;
  epicCompletionRate: number;
  
  // Performance metrics
  averageCompletionTime: number;
  
  // Project health indicators
  healthScore: number;
}

export interface DashboardOverview {
  myIssues: {
    total: number;
    active: number;
    overdue: number;
    issues: Issue[];
  };
  activeSprints: Sprint[];
  recentActivity: Issue[];
  projectStats: Array<{
    project: Project;
    totalIssues: number;
    completedIssues: number;
    activeIssues: number;
    completionRate: number;
  }>;
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: any[];
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AuthResponse {
  user: User;
  token: string;
  expiresIn: string;
}

// Form Types
export interface CreateProjectData {
  name: string;
  key: string;
  storyPrefix?: string;
  description?: string;
  methodology: 'scrum' | 'kanban' | 'hybrid';
  leadId?: string;
  timeTrackerProjectId?: string;
}

export interface CreateIssueData {
  title: string;
  description?: string;
  type: IssueType;
  priority: Priority;
  status?: string;
  projectId: string;
  assigneeId?: string;
  epicId?: string;
  sprintId?: string;
  parentId?: string;
  storyPoints?: number;
  originalEstimate?: number;
  labels?: string[];
  dueDate?: string;
  customFields?: any;
}

export interface CreateEpicData {
  name: string;
  description?: string;
  status: string;
  priority: Priority;
  projectId: string;
  ownerId?: string;
  releaseId?: string;
  goals?: string[];
  successMetrics?: Array<{
    metric: string;
    target: string;
    current?: string;
  }>;
  color?: string;
  targetDate?: string;
}

export interface CreateSprintData {
  name: string;
  goal?: string;
  startDate: string;
  endDate: string;
  projectId: string;
  capacity?: number;
  releaseId?: string;
  epicIds?: string[];
}

// Filter Types
export interface IssueFilters {
  parentId?: string;
  projectId?: string;
  assigneeId?: string;
  reporterId?: string;
  status?: IssueStatus[];
  type?: IssueType[];
  priority?: Priority[];
  sprintId?: string;
  epicId?: string;
  search?: string;
  labels?: string[];
  page?: number;
  limit?: number;
}